import React, { useState } from 'react';
import CollapsibleContent from '../common/CollapsibleContent';
import { ChildQuote, Quote } from '../../types/quote';
import { useTranslation } from 'react-i18next';
import FormattedCurrency from '../common/FormattedCurrency';
import { QuoteJobInfo } from './QuoteJobInfo';
import { Button } from '../common/Button';

import { computeQuoteAmount, computeQuoteTotalAmount } from '../../utils/jobUtils';
import QuoteFreelancerCard from './QuoteFreelancerCard';
import QuoteConfirmRetireModal from './QuoteConfirmRetiredModal';
import { getQuoteStatus, isQuoteActive } from '../../utils/quoteUtils';
import FreelancerProfileModal from '../freelancers/FreelancerProfileModal';
import { FreelancerSearchResult } from '../../types/search';
import SingleSubmissionModal from './SingleSubmissionModal';

interface QuoteListCardProps {
  quote: Quote;
  onEdit: (quote: Quote) => void;
  expanded?: boolean;
  projectMode?: boolean;
}

const QuoteListCard: React.FC<QuoteListCardProps> = ({
  quote,
  onEdit,
  expanded = false,
  projectMode = false,
}) => {
  const { t } = useTranslation(['quotes', 'common', 'hub']);

  // Internal modal states
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedQuoteToView, setSelectedQuoteToView] = useState<Quote | ChildQuote | null>(null);
  const [isConfirmRetire, setIsConfirmRetire] = useState(false);
  const [selectedFreelancer, setSelectedFreelancer] = useState<FreelancerSearchResult | null>(null);

  const hasAlliances = quote.alliances && quote.alliances.length > 0;

  const isQuoteAccepted = quote.status === 'accepted';
  const isQuoteSubmitted = quote.status === 'submitted';

  const isActiveQuote = isQuoteActive(quote);

  const isQuoteEditable = isActiveQuote && !projectMode;
  const isQuoteCancelable =
    (isActiveQuote || quote.parent_quote_status === 'draft' || isQuoteSubmitted) && !projectMode;

  const quoteStatus = getQuoteStatus(quote);

  const quoteStatusLabel = projectMode
    ? t(`job_statuses.${quote.job.status}`, { ns: 'common' })
    : t(`quote_statuses.${quoteStatus}`, { ns: 'common' });

  const ownershipStatusLabel = quote.parent_quote_owner
    ? t('quote_ownership_statuses.alliance', { ns: 'quotes' })
    : t('quote_ownership_statuses.principal', { ns: 'quotes' });

  const jobHeader = (
    <QuoteJobInfo
      quoteJob={quote.job}
      headerOnly={true}
      statusLabel={quoteStatusLabel}
      ownershipStatusLabel={ownershipStatusLabel}
    />
  );
  const jobDetails = <QuoteJobInfo quoteJob={quote.job} detailsOnly={true} />;

  // Compute totals directly from quote data
  const myQuoteTotal = computeQuoteAmount(quote);
  const grandTotal = computeQuoteTotalAmount(quote);

  const handleViewQuote = (quoteToView: Quote | ChildQuote) => {
    setSelectedQuoteToView(quoteToView);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedQuoteToView(null);
  };

  return (
    <>
      <CollapsibleContent
        className="flex flex-col rounded-2xl border-2 border-gray-300 bg-white p-4 shadow shadow-xl transition-all duration-300"
        header={jobHeader}
        defaultExpanded={expanded}
        contentClassName="bg-white"
        headerClassName="items-start"
      >
        {jobDetails}

        <hr className="my-4 border-[#E1E9F0]" />

        <div className="mt-4">
          <h3 className="mb-2 text-lg font-bold text-secondary">
            {t('my_submission', { ns: 'quotes', defaultValue: 'Ma soumission' })}
          </h3>
          <div className="mb-2 flex w-full flex-col gap-8 md:flex-row md:items-center md:justify-between">
            <span className="text-3xl font-bold text-primary">
              <FormattedCurrency value={myQuoteTotal} />
            </span>
            <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
              <Button
                variant="transparent-with-border"
                className="w-full md:w-auto md:px-8"
                href={`/projects/${quote.job_id}?selectedQuoteId=${quote.id}`}
              >
                {t('viewHub', { ns: 'hub', defaultValue: 'View hub' })}
              </Button>
              {!isQuoteEditable && (
                <Button
                  variant="primary"
                  className="w-full md:w-auto md:px-8"
                  onClick={() => handleViewQuote(quote)}
                >
                  {t('viewSubmissionButtonLabel', { ns: 'quotes' })}
                </Button>
              )}

              {isQuoteEditable && (
                <Button
                  variant={'primary'}
                  className="w-full md:w-auto md:px-8"
                  onClick={() => onEdit(quote)}
                >
                  {t('edit_submission', { ns: 'quotes', defaultValue: 'Modifier ma soumission' })}
                </Button>
              )}

              {isQuoteCancelable && (
                <Button
                  variant="simple-text"
                  className="w-full md:w-auto md:px-8"
                  onClick={() => setIsConfirmRetire(true)}
                >
                  {t('cancel_submission', { ns: 'quotes', defaultValue: 'Annuler ma soumission' })}
                </Button>
              )}
            </div>
          </div>
        </div>

        {hasAlliances && (
          <>
            <hr className="my-4 border-[#E1E9F0]" />
            <div className="mb-4">
              {quote.alliances && quote.alliances.length > 0 && (
                <h3 className="mb-2 text-lg font-bold text-secondary">
                  {t('your_alliances', { ns: 'quotes', defaultValue: 'Vos Alliances' })}
                </h3>
              )}

              <div className="flex flex-col gap-4">
                {quote.alliances?.map((quoteAlliance, index) => {
                  const isLast = index === (quote.alliances?.length || 0) - 1;

                  return (
                    <QuoteFreelancerCard
                      key={quoteAlliance.id}
                      freelancer={{
                        id: quoteAlliance.invited_freelancer.profile_id,
                        first_name: quoteAlliance.invited_freelancer.first_name,
                        last_name: quoteAlliance.invited_freelancer.last_name,
                        avatar_url: quoteAlliance.invited_freelancer.avatar_url,
                        service_category_slugs: [quoteAlliance.service_category.slug],
                        service_categories: [quoteAlliance.service_category],
                        full_access: true,
                      }}
                      quoteAlliance={quoteAlliance}
                      className={`px-0 md:px-0 ${isLast ? '' : 'border-b border-[#E0E0E0]'}`}
                      onClick={
                        quoteAlliance.child_quote
                          ? () => {
                              if (quoteAlliance.child_quote) {
                                handleViewQuote(quoteAlliance.child_quote);
                              }
                            }
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            </div>

            <hr className="my-4 border-[#E1E9F0]" />

            <div className="my-4">
              <h3 className="mb-2 text-lg font-bold text-secondary">
                {t('grand_total', { ns: 'quotes', defaultValue: 'Grand total' })}
              </h3>
              <div className="mb-2 flex w-full flex-col gap-8 md:flex-row md:items-center md:justify-between">
                <span className="text-3xl font-bold text-primary">
                  <FormattedCurrency value={grandTotal} />
                </span>

                <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
                  {isQuoteAccepted && !projectMode && (
                    <Button
                      className="w-full md:w-auto md:px-8"
                      variant="primary"
                      href={`/projects`}
                    >
                      {t('viewProjectButtonLabel', { ns: 'quotes' })}
                    </Button>
                  )}
                  {isQuoteCancelable && (
                    <Button
                      variant="simple-text"
                      className="w-full md:w-auto md:px-8"
                      onClick={() => setIsConfirmRetire(true)}
                    >
                      {t('cancel_submission', {
                        ns: 'quotes',
                        defaultValue: 'Annuler ma soumission',
                      })}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {selectedFreelancer && (
          <FreelancerProfileModal
            freelancer={selectedFreelancer}
            onClose={() => setSelectedFreelancer(null)}
          />
        )}

        {isConfirmRetire && (
          <QuoteConfirmRetireModal quote={quote} onClose={() => setIsConfirmRetire(false)} />
        )}
      </CollapsibleContent>

      {/* View Modal */}
      {isViewModalOpen && selectedQuoteToView && (
        <SingleSubmissionModal
          onClose={handleCloseViewModal}
          job={quote.job}
          quote={selectedQuoteToView}
          parentQuote={quote}
        />
      )}
    </>
  );
};

export default QuoteListCard;
